import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Page from "../../components/Common/Page";
import PagesHeader from "../../components/Common/PagesHeader";

// Platforms
import {
  FloatRight as PlatformFloatRight,
  ListView as PlatformListView,
} from "../../components/Platforms";
import { getPlatformsList } from "../../appRedux/actions/platforms";

// Offers
import {
  FloatRight as OfferFloatRight,
  ListView as OfferListView,
} from "../../components/Offers";
import { getOffersList } from "../../appRedux/actions/offers";

// Subscriptions
import {
  FloatRight as SubscriptionFloatRight,
  ListView as SubscriptionListView,
} from "../../components/Subscriptions";
import { getSubscriptionsList } from "../../appRedux/actions/subscriptions";

const PlatformHub = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tabs: "platforms", "offers", "subscriptions"
  const currentTab = searchParams.get("tab") || "platforms";
  
  // Search text states for each tab
  const [platformInitialFetch, setPlatformInitialFetch] = useState(false);
  const [platformSearch, setPlatformSearch] = useState("");
  const [offerInitialFetch, setOfferInitialFetch] = useState(false);
  const [offerSearch, setOfferSearch] = useState("");
  const [subscriptionInitialFetch, setSubscriptionInitialFetch] = useState(false);
  const [subscriptionSearch, setSubscriptionSearch] = useState("");




  useEffect(() => {
    if (currentTab === "platforms") {
      dispatch(getPlatformsList({ page: 1, size: 10 })).then(() => {
        setPlatformInitialFetch(true);
      });
    } else if (currentTab === "offers") {
      dispatch(getOffersList({ page: 1, size: 10 })).then(() => {
        setOfferInitialFetch(true);
      });
    } else if (currentTab === "subscriptions") {
      dispatch(getSubscriptionsList({ page: 1, size: 10 })).then(() => {
        setSubscriptionInitialFetch(true);
      });
    }
  }, [dispatch, currentTab]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (currentTab === "platforms" && platformInitialFetch) {
        dispatch(
          getPlatformsList({
            page: 1,
            size: 10,
            title: platformSearch || undefined,
          })
        );
      }

      if (currentTab === "offers" && offerInitialFetch) {
        dispatch(
          getOffersList({
            page: 1,
            size: 10,
            search: offerSearch || undefined,
          })
        ).catch(() => {});
      }

      if (currentTab === "subscriptions" && subscriptionInitialFetch) {
        dispatch(
          getSubscriptionsList({
            page: 1,
            size: 10,
            search: subscriptionSearch || undefined,
          })
        ).catch(() => {});
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    currentTab,
    dispatch,
    offerInitialFetch,
    offerSearch,
    platformInitialFetch,
    platformSearch,
    subscriptionInitialFetch,
    subscriptionSearch,
  ]);

  const handleTabChange = (tabName) => {
    setSearchParams({ tab: tabName });
  };

  const hubTabs = [
    { key: "platforms", label: "Platforms", icon: "fa fa-cubes" },
    { key: "offers", label: "Offers", icon: "fa fa-tags" },
    { key: "subscriptions", label: "Subscriptions", icon: "fa fa-refresh" },
  ];

  const getSearchValue = () => {
    if (currentTab === "offers") return offerSearch;
    if (currentTab === "subscriptions") return subscriptionSearch;
    return platformSearch;
  };

  const setSearchValue = (value) => {
    if (currentTab === "offers") {
      setOfferSearch(value);
      return;
    }
    if (currentTab === "subscriptions") {
      setSubscriptionSearch(value);
      return;
    }
    setPlatformSearch(value);
  };

  const getSearchPlaceholder = () => {
    if (currentTab === "offers") return "Search by title, merchant, or status...";
    if (currentTab === "subscriptions") return "Search by title, merchant, or status...";
    return "Search by platform name or code...";
  };

  const renderHeaderActions = () => {
    switch (currentTab) {
      case "offers":
        return <OfferFloatRight />;
      case "subscriptions":
        return <SubscriptionFloatRight />;
      case "platforms":
      default:
        return <PlatformFloatRight viewStatus="listView" onChange={() => {}} />;
    }
  };

  return (
    <Page title="Platforms Hub">
      <div className="page-wrapper enterprise-management-page">
        <div className="content container-fluid">
          <PagesHeader
            pageTitle="Platforms Hub"
            homePageUrl="/"
            homePageText="Dashboard"
            activePageText="Platforms"
            floatRight={renderHeaderActions()}
          />

          <div className="enterprise-hub-toolbar">
            <div className="enterprise-hub-tabs" role="tablist" aria-label="Platform hub sections">
              {hubTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`enterprise-hub-tab ${currentTab === tab.key ? "active" : ""}`}
                  onClick={() => handleTabChange(tab.key)}
                  role="tab"
                  aria-selected={currentTab === tab.key}
                >
                  <i className={tab.icon} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            <div className="enterprise-hub-search">
              <i className="fa fa-search" aria-hidden="true" />
              <input
                type="search"
                value={getSearchValue()}
                placeholder={getSearchPlaceholder()}
                onChange={(event) => setSearchValue(event.target.value)}
                aria-label={getSearchPlaceholder()}
              />
              {getSearchValue() && (
                <button
                  type="button"
                  className="enterprise-search-clear"
                  onClick={() => setSearchValue("")}
                  aria-label="Clear search"
                >
                  <i className="fa fa-times" />
                </button>
              )}
            </div>
          </div>

          <div className="tab-content">
            {currentTab === "platforms" && (
              <div className="tab-pane fade show active">
                <div className="enterprise-table-panel">
                  <PlatformListView />
                </div>
              </div>
            )}

            {currentTab === "offers" && (
              <div className="tab-pane fade show active">
                <div className="enterprise-table-panel">
                  <OfferListView />
                </div>
              </div>
            )}

            {currentTab === "subscriptions" && (
              <div className="tab-pane fade show active">
                <div className="enterprise-table-panel">
                  <SubscriptionListView />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default PlatformHub;
